/**
 * The range the owner picks on one page, carried to every other page with a range picker (the `studioPeriod` slice).
 * It is kept as a Home / Stats period ({@link HomePeriod}), the widest vocabulary; the pages whose pickers offer less
 * read it as closely as they can show it, and a pick there is written back only when Home can show it too:
 *  - Stats › Website: presets up to 90 days and custom runs of up to 90 days ending by today in UTC (UTC days);
 *  - Affiliates reports and a marketing asset's stats: This month, 7 / 30 / 90 days and any run of dates.
 *
 * The Affiliate Studio console (the Affiliates page's "Advanced Affiliates Dashboard") opens on it too, read as the
 * reports' picker reads it: the console counts days the same way.
 *
 * Pure (no store, no React), so the translations are unit-tested.
 */
import type { HomeInterval, HomePeriod } from '../../mock-data/types';
import { rangeFromPreset, type DateRange, type PeriodPreset } from '../affiliates/reports-helpers';
import { MAX_RANGE_DAYS, WEBSITE_INTERVALS, customRange, rangeDays, type WebsitePeriod } from '../website-analytics/queries';
import { customDays, customPeriod, homeWindow, isHomeInterval, localToday } from './stats-mapping';

const MS_DAY = 86_400_000;

const dayAt = (ymd: string): number => Date.parse(`${ymd}T00:00:00Z`);
const ymdOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** The run of days `from`..`to` (both included) as a Home period, or null when Home cannot show it: a run that starts
 *  after today, a single day other than today or yesterday, or a run past its longest. A run that ends after today
 *  reads to today (Home's picker ends there; a reports date field does not). Pure but for the clock. */
export const homePeriodOfDays = (from: string, to: string, nowMs: number = Date.now()): HomePeriod | null => {
  const today = localToday(nowMs);
  if (from > today) return null;
  const last = to > today ? today : to;
  if (from === last) {
    if (from === today) return 'TODAY';
    if (from === ymdOf(dayAt(today) - MS_DAY)) return 'YESTERDAY';
    return null;
  }
  const period = customPeriod(from, last);
  return customDays(period) ? period : null;
};

/* ---------- Stats › Website ---------- */

/**
 * A Home period as the Website tab shows it: its own presets as they are, 6 months and a year as the last 90 days (the
 * tab's longest), and a custom run as it is, its last day held to today in UTC (the analytics service's day) and a run
 * longer than 90 days cut to its own last 90 days. Pure but for the clock.
 */
export const websitePeriodFor = (period: HomePeriod, nowMs: number = Date.now()): WebsitePeriod => {
  if ((WEBSITE_INTERVALS as readonly string[]).includes(period)) return period as WebsitePeriod;
  const range = customDays(period);
  if (!range) return '90D';
  const utcToday = ymdOf(nowMs);
  const to = range.to > utcToday ? utcToday : range.to;
  const earliest = ymdOf(dayAt(to) - (MAX_RANGE_DAYS - 1) * MS_DAY);
  const from = range.from < earliest ? earliest : range.from;
  if (from > to) return 'TODAY';
  const website = customRange(from, to);
  return rangeDays(website, nowMs) !== null ? website : '90D';
};

/** A Website tab pick as a Home period, or null when Home cannot show it (a single past day). Pure but for the clock. */
export const homePeriodOfWebsite = (period: WebsitePeriod, nowMs: number = Date.now()): HomePeriod | null => {
  if (isHomeInterval(period)) return period;
  const m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(period);
  if (!m) return null;
  // The tab's days are UTC days, and so are Home's Today and Yesterday: a single day is matched in UTC, not against the
  // viewer's calendar (the two differ for hours every day away from UTC).
  if (m[1] === m[2]) {
    const utcToday = ymdOf(nowMs);
    if (m[1] === utcToday) return 'TODAY';
    if (m[1] === ymdOf(dayAt(utcToday) - MS_DAY)) return 'YESTERDAY';
    return null;
  }
  // Shared as picked: the tab already ends its runs by today in UTC, which west of UTC is the viewer's tomorrow for
  // part of the evening, and Home reads such a run live (holding it to the viewer's today would drop the live day).
  const run = customPeriod(m[1], m[2]);
  return customDays(run) ? run : null;
};

/* ---------- Affiliates reports and a marketing asset's stats ---------- */

export type ReportPeriod = { preset: PeriodPreset; range: DateRange };

const REPORT_PRESET: Partial<Record<HomeInterval, Exclude<PeriodPreset, 'custom' | 'month'>>> = {
  '7D': '7d',
  '30D': '30d',
  '90D': '90d',
};

/**
 * A Home period as the reports' period picker shows it: 7, 30 and 90 days as its own presets, this month so far as
 * This month, and anything else as its run of dates (the viewer's calendar days, both included). Pure but for the
 * clock.
 */
export const reportPeriodFor = (period: HomePeriod, nowMs: number = Date.now()): ReportPeriod => {
  const today = new Date(nowMs);
  const preset = isHomeInterval(period) ? REPORT_PRESET[period] : undefined;
  if (preset) return { preset, range: rangeFromPreset(preset, today) };
  const todayYmd = localToday(nowMs);
  let range: DateRange;
  if (period === 'TODAY') range = { from: todayYmd, to: todayYmd };
  else if (period === 'YESTERDAY') {
    const yesterday = ymdOf(dayAt(todayYmd) - MS_DAY);
    range = { from: yesterday, to: yesterday };
  } else {
    // Every other period is a run of local dates, end-exclusive.
    const w = homeWindow(period, nowMs);
    range = { from: w.startDate, to: ymdOf(dayAt(w.endDate) - MS_DAY) };
  }
  // A run shared from a UTC day can end on the viewer's tomorrow; the reports' days are the viewer's, ending today.
  if (range.to > todayYmd) range = { from: range.from > todayYmd ? todayYmd : range.from, to: todayYmd };
  const month = rangeFromPreset('month', today);
  if (range.from === month.from && range.to === month.to) return { preset: 'month', range: month };
  return { preset: 'custom', range };
};

/** A reports pick as a Home period, or null when Home cannot show it (a single past day). Pure but for the clock. */
export const homePeriodOfReport = ({ preset, range }: ReportPeriod, nowMs: number = Date.now()): HomePeriod | null => {
  if (preset === '7d') return '7D';
  if (preset === '30d') return '30D';
  if (preset === '90d') return '90D';
  return homePeriodOfDays(range.from, range.to, nowMs);
};

/* ---------- Affiliate Studio console ---------- */

/**
 * The range the console opens on from the Affiliates page: the shared one as the reports' picker reads it (the console
 * counts the viewer's calendar days too, both included, ending today), else This month. Pure but for the clock.
 */
export const consolePeriodFor = (period: HomePeriod | null, nowMs: number = Date.now()): ReportPeriod =>
  period !== null ? reportPeriodFor(period, nowMs) : { preset: 'month', range: rangeFromPreset('month', new Date(nowMs)) };
